"use client";

import { ArrowRight, FileText, Leaf, Scale } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  icon: Scale,
  title: "Integrity",
  lineOne: "We uphold the highest ethical standards in every financial transaction.",
  lineTwo: "Honest communication and clear accountability define our client relationships."
}, {
  icon: FileText,
  title: "Excellence",
  lineOne: "Precision-driven processes ensure accuracy in every report and audit.",
  lineTwo: "Continuous improvement drives our commitment to superior service delivery."
}, {
  icon: Leaf,
  title: "Stewardship",
  lineOne: "Responsible management of resources for long-term sustainable growth.",
  lineTwo: "Investing in community initiatives that foster lasting positive impact."
}];
export default function Values() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="15926" id="values-mission" className="bg-[var(--color-background)] py-32 md:py-44">
      <div className="container mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className="lg:sticky lg:top-32 transition-all duration-[620ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" style={{
            opacity: leftVisible ? 1 : 0,
            transform: leftVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <p className="text-xs font-semibold uppercase text-[var(--color-primary-500)] tracking-[0.16em] font-sans">
                Our Core Values
              </p>
              <div className="mt-4 h-px bg-[var(--color-accent-500)] transition-all duration-[520ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" style={{
              width: leftVisible ? "48px" : "0px"
            }} />

              <h2 className="mt-8 font-default font-normal text-[var(--color-foreground)] leading-[1.08]" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
              letterSpacing: "-0.018em"
            }}>
                Principles <em className="font-default italic">guiding</em> our path forward.
              </h2>

              <div className="mt-8 space-y-5 font-sans text-[var(--color-primary-700)] leading-[1.6]" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              letterSpacing: "-0.005em"
            }}>
                <p>
                  Our organization is built on a foundation of trust and a 
                  relentless pursuit of quality in everything we undertake.
                </p>
                <p>
                  We integrate strategic foresight with disciplined execution, 
                  ensuring that our mission remains aligned with the evolving 
                  needs of our stakeholders.
                </p>
                <p>
                  By prioritizing long-term value over short-term gains, we 
                  create a legacy of reliability and professional excellence.
                </p>
              </div>

              <div className="mt-8">
                <Link to="/AboutKTEXExpandedPage" className="group inline-flex items-center gap-2 font-sans text-sm font-medium text-[var(--color-foreground)]">
                  <span className="relative pb-1">
                    View our full mission statement
                    <span className="absolute left-0 bottom-0 h-px w-full bg-[var(--color-accent-500)] transition-all duration-200 group-hover:h-[2px]" />
                  </span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="border-t border-[var(--color-primary-200)]">
              {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return <PillarRow key={pillar.title} data-index={i} index={i} Icon={Icon} title={pillar.title} lineOne={pillar.lineOne} lineTwo={pillar.lineTwo} />;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type PillarRowProps = {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  lineOne: string;
  lineTwo: string;
  "data-index"?: number;
};
function PillarRow({
  index,
  Icon,
  title,
  lineOne,
  lineTwo,
  ...rest
}: PillarRowProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className="group grid grid-cols-12 gap-6 py-10 md:py-12 border-b border-[var(--color-primary-200)] transition-colors duration-200 hover:border-[var(--color-primary-400)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 620ms cubic-bezier(0.22,0.61,0.36,1) ${index * 80}ms, transform 620ms cubic-bezier(0.22,0.61,0.36,1) ${index * 80}ms, border-color 200ms ease-out`
  }}>
      <div className="col-span-2 flex items-start justify-start pt-1">
        <Icon className="h-8 w-8 text-[var(--color-primary-500)] transition-colors duration-200 group-hover:text-[var(--color-accent-500)]" strokeWidth={1.25} />
      </div>
      <div className="col-span-10">
        <h3 className="font-default font-semibold text-[var(--color-foreground)] leading-[1.35]" style={{
        fontSize: "clamp(1rem, 1.1vw, 1.125rem)"
      }}>
          {title}
        </h3>
        <div className="mt-3 space-y-1.5 font-sans text-base text-[var(--color-primary-700)] leading-[1.65]">
          <p>{lineOne}</p>
          <p>{lineTwo}</p>
        </div>
      </div>
    </div>;
}